
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { Loader2, Check, ChevronsUpDown, X, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { cn, cleanFormData, getErrorMessage, ErrorMessages } from '@/lib/utils.ts';
import { serviceFactory } from '@/services/core/serviceFactory.ts';
import { PatientEnrollerService, CreatePatientEnrollerPayload } from '@/services/features/PatientEnrollerService.ts';
import { DropdownDataService, Clinic as ServiceClinic } from '@/services/features/DropdownDataService.ts';

interface Clinic {
  clinic_id: number;
  clinic_name: string;
  patient_clinic_timezone: string;
  has_active_enroller: boolean;
}

interface AddPatientEnrollerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const AddPatientEnrollerDialog: React.FC<AddPatientEnrollerDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deferredTask, setDeferredTask] = useState(false);
  const [followUpTask, setFollowUpTask] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [selectedClinicIds, setSelectedClinicIds] = useState<number[]>([]);
  const [clinicSearchOpen, setClinicSearchOpen] = useState(false);
  const [clinicSearch, setClinicSearch] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const { user, token, hasuraToken } = useAuth();

  // Initialize service
  const patientEnrollerService = serviceFactory.getService(PatientEnrollerService);

  // Update service context when auth changes
  useEffect(() => {
    if (user && token && hasuraToken) {
      patientEnrollerService.updateContext({
        userId: user.id,
        token: token,
        hasuraToken: hasuraToken,
        userType: user.type
      });
    }
  }, [user, token, hasuraToken, patientEnrollerService]);

  useEffect(() => {
    if (open && hasuraToken) {
      fetchClinics();
    }
    // Reset form when dialog opens
    if (open) {
      resetForm();
    }
  }, [open, hasuraToken]);

  const fetchClinics = async () => {
    if (!hasuraToken) {
      toast({
        title: "Error",
        description: "Authentication token not available. Please sign in again.",
        variant: "destructive",
      });
      return;
    }

    try {
      const dropdownService = serviceFactory.createService(DropdownDataService, {
        hasuraToken
      });

      const result = await dropdownService.fetchAvailableClinics();
      
      if (result.success && result.data?.pep_vw_available_clinics) {
        // Convert ServiceClinic to local Clinic interface
        const clinicsData = result.data.pep_vw_available_clinics.map((clinic: ServiceClinic) => ({
          ...clinic,
          has_active_enroller: false // Default value since service doesn't provide this
        }));
        setClinics(clinicsData);
      } else if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        toast({
          title: "Error",
          description: "Failed to load clinics - authentication error",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching clinics:', error);
      toast({
        title: "Error",
        description: "Failed to load clinics",
        variant: "destructive",
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!firstName.trim()) {
      newErrors.firstName = ErrorMessages.REQUIRED_FIELD;
    } else if (firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    if (!lastName.trim()) {
      newErrors.lastName = ErrorMessages.REQUIRED_FIELD;
    } else if (lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    if (!email.trim()) {
      newErrors.email = ErrorMessages.REQUIRED_FIELD;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        newErrors.email = ErrorMessages.INVALID_EMAIL;
      }
    }

    if (!password.trim()) {
      newErrors.password = ErrorMessages.REQUIRED_FIELD;
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (selectedClinicIds.length === 0) {
      newErrors.clinics = 'Please select at least one clinic';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClinicToggle = (clinicId: number) => {
    setSelectedClinicIds(prev => 
      prev.includes(clinicId)
        ? prev.filter(id => id !== clinicId)
        : [...prev, clinicId]
    );
    // Clear clinic error when user selects a clinic
    if (errors.clinics) {
      setErrors(prev => ({ ...prev, clinics: '' }));
    }
  };

  const removeSelectedClinic = (clinicId: number) => {
    setSelectedClinicIds(prev => prev.filter(id => id !== clinicId));
  };

  const filteredClinics = clinics.filter(clinic =>
    clinic.clinic_name.toLowerCase().includes(clinicSearch.toLowerCase())
  );

  const selectedClinics = clinics.filter(c => selectedClinicIds.includes(c.clinic_id));

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setDeferredTask(false);
    setFollowUpTask(false);
    setSelectedClinicIds([]);
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSubmit = async () => {
    if (!user?.id || !token) {
      toast({
        title: "Authentication Error",
        description: ErrorMessages.SESSION_EXPIRED,
        variant: "destructive"
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Clean form data before sending
      const cleanedData = cleanFormData({
        firstName,
        lastName,
        email,
        password
      });

      // Prepare API data format using service interface
      const payload: CreatePatientEnrollerPayload = {
        first_name: cleanedData.firstName,
        last_name: cleanedData.lastName,
        email: cleanedData.email,
        password: cleanedData.password,
        clinic_ids: selectedClinicIds,
        deferred_task: deferredTask ? "true" : "false",
        follow_up_task: followUpTask ? "true" : "false"
      };

      console.log('Creating Patient Enroller with payload:', payload);

      const result = await patientEnrollerService.createPatientEnroller(payload);
      console.log('PatientEnroller creation result:', result);
      
      if (result.success) {
        toast({
          title: "Success",
          description: "Patient Enrollment Specialist created successfully!",
        });
        resetForm();
        onSuccess();
        onOpenChange(false);
      } else {
        throw new Error(result.message || 'Failed to create Patient Enrollment Specialist');
      }
    } catch (err) {
      console.error('Error creating Patient Enrollment Specialist:', err);
      toast({
        title: "Creation Failed",
        description: (err as Error).message || "Unable to create specialist account. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            Add Patient Enrollment Specialist
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-sm font-medium">
                First Name
              </Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (errors.firstName) setErrors(prev => ({ ...prev, firstName: '' }));
                }}
                placeholder="First Name"
                className={`w-full ${errors.firstName ? 'border-red-500' : ''}`}
                autoComplete="off"
              />
              {errors.firstName && <p className="text-red-500 text-sm">{errors.firstName}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-sm font-medium">
                Last Name
              </Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (errors.lastName) setErrors(prev => ({ ...prev, lastName: '' }));
                }}
                placeholder="Last Name"
                className={`w-full ${errors.lastName ? 'border-red-500' : ''}`}
                autoComplete="off"
              />
              {errors.lastName && <p className="text-red-500 text-sm">{errors.lastName}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
              }}
              placeholder="Email Address"
              className={`w-full ${errors.email ? 'border-red-500' : ''}`}
              autoComplete="new-password"
            />
            {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
          </div>

          <div className="space-y-3">
            <Label>Assigned Tasks :</Label>
            <div className="flex space-x-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="deferredTask"
                  checked={deferredTask}
                  onCheckedChange={(checked) => setDeferredTask(checked as boolean)}
                />
                <Label htmlFor="deferredTask" className="text-sm">
                  Deferred
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="followUpTask"
                  checked={followUpTask}
                  onCheckedChange={(checked) => setFollowUpTask(checked as boolean)}
                />
                <Label htmlFor="followUpTask" className="text-sm">
                  Follow Up
                </Label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
                  }}
                  placeholder="Password"
                  className={`w-full pr-10 ${errors.password ? 'border-red-500' : ''}`}
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: '' }));
                  }}
                  placeholder="Confirm Password"
                  className={`w-full pr-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.confirmPassword && <p className="text-red-500 text-sm">{errors.confirmPassword}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clinic">Select Clinic</Label>
            
            {/* Selected Clinics Display */}
            {selectedClinics.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50">
                {selectedClinics.map((clinic) => (
                  <div
                    key={clinic.clinic_id}
                    className="flex items-center space-x-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                  >
                    <span>{clinic.clinic_name}</span>
                    <button
                      type="button"
                      onClick={() => removeSelectedClinic(clinic.clinic_id)}
                      className="hover:bg-blue-200 rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Popover open={clinicSearchOpen} onOpenChange={setClinicSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={clinicSearchOpen}
                  className={`w-full justify-between ${errors.clinics ? 'border-red-500' : ''}`}
                  type="button"
                >
                  {selectedClinicIds.length > 0
                    ? `${selectedClinicIds.length} clinic(s) selected`
                    : "Select Clinic"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-[--radix-popover-trigger-width] p-0 bg-white border shadow-lg z-[9999] max-h-80"
                align="start"
                sideOffset={4}
              >
                <div className="p-3 border-b bg-white">
                  <Input
                    placeholder="Search clinics..."
                    value={clinicSearch}
                    onChange={(e) => setClinicSearch(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto p-2 bg-white">
                  {clinics.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">Loading clinics...</div>
                  ) : filteredClinics.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No clinics found</div>
                  ) : (
                    // Show selected clinics first
                    [...filteredClinics]
                      .sort((a, b) => {
                        const aSelected = selectedClinicIds.includes(a.clinic_id);
                        const bSelected = selectedClinicIds.includes(b.clinic_id);
                        if (aSelected && !bSelected) return -1;
                        if (!aSelected && bSelected) return 1;
                        return a.clinic_name.localeCompare(b.clinic_name);
                      })
                      .map((clinic) => (
                        <div
                          key={clinic.clinic_id}
                          className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          onClick={() => handleClinicToggle(clinic.clinic_id)}
                        >
                          <Checkbox
                            checked={selectedClinicIds.includes(clinic.clinic_id)}
                            onCheckedChange={() => {}} 
                            className="flex-shrink-0"
                          />
                          <span className="text-sm flex-1">{clinic.clinic_name}</span>
                        </div>
                      ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
            {errors.clinics && <p className="text-red-500 text-sm">{errors.clinics}</p>}
          </div>
        </div>

        <div className="flex space-x-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddPatientEnrollerDialog;
